// ====================================================================
// OBBY PEAK — a climbable obstacle course to the north (the jump test)
// ====================================================================
// The movement-playground zone: a stone mountain you climb by hopping a spiral
// of platforms to a treasure summit. Built to exercise the jump AND the double
// jump (src/main.js: jumpVel 7.6 / gravityUp -19 → a single jump tops out ~1.5
// units, a double reaches ~3), so two "gate" platforms sit a full 2.0 units
// above their neighbour — unreachable with one jump, easy with two.
//
// Same machinery as the other zones (Wordville, the Grove): its flat base is a
// plateau mask in the heightfield (centred at (0, 44)); its scatter dressing is
// chunk props via the ctx builders; the climb itself is procedural boxes merged
// by colour into a handful of draw calls, each with one collision AABB whose
// TOP is what the player physics treats as standable ground.
//
// Cozy by design: no damage, no death. Miss a jump and you just drop to a lower
// ledge or the base and try again. The summit grants the Mountaineer Cap
// (src/game/outfits.js) the first time you reach it — same owned-list reward
// pattern as the Lucky Straw Hat (src/game/quest.js).
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../../core/tier.js';
import { addObstacle } from '../collision.js';
import { markDynamic } from '../freezeStatics.js';
import { equip, getEquipped } from '../../game/outfits.js';
import { loadSave, patchSave } from '../../game/save.js';

export const OBBY_CENTER = { x: 0, z: 44 };

// Colours
const STONE = 0x9a9488;   // pillar + normal platforms
const BASE  = 0x88927a;   // mossy entrance pad
const GATE  = 0xe8a13c;   // the two double-jump platforms (read as "special")
const CHECK = 0x70c7ff;   // checkpoint/rest ledges (kid-friendly recovery reads)
const GUIDE = 0xb8a9ff;   // low guide rails and route arrows
const SNOW  = 0xeef2f5;   // snowy summit pad
const PILLAR_TOP = 9.1;   // the central mass rises to just under the summit
export const OBBY_SUMMIT_TOP = 9.4;   // standable surface of the summit pad
export const OBBY_SUMMIT_R = 1.8;     // XZ radius that counts as "on the summit"

let _ctx = null;
let _chest = null;
let _summitArmed = true;    // re-arms after the player descends, so re-summits greet again

// ---- geometry batching: merge all the stone boxes by colour ---------------
const _batch = new Map();   // hex -> [BufferGeometry]
function tinted(geo, hex) {
  const c = new THREE.Color(hex);
  const n = geo.attributes.position.count;
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b; }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}
// A solid box by CENTRE y. Collidable boxes register one AABB (top = standable).
function solid(x, yc, z, w, h, d, hex, collide = true) {
  const g = tinted(new THREE.BoxGeometry(w, h, d), hex);
  g.translate(x, yc, z);
  if (!_batch.has(hex)) _batch.set(hex, []);
  _batch.get(hex).push(g);
  if (collide) {
    addObstacle({ minX: x - w / 2, maxX: x + w / 2, minY: yc - h / 2, maxY: yc + h / 2, minZ: z - d / 2, maxZ: z + d / 2 });
  }
}
// A platform by its SURFACE height (what the player stands on).
const PLAT_H = 0.5;
function platform(x, z, topY, size, hex) {
  solid(x, topY - PLAT_H / 2, z, size, PLAT_H, size, hex, true);
}

// Decorative rotated box (non-collidable): used for the route ribbon and arrows
// between pads. It batches with the other obby boxes but never enters collision,
// so it costs no runtime physics work.
function decoBox(x, yc, z, w, h, d, yaw, hex) {
  const g = tinted(new THREE.BoxGeometry(w, h, d), hex);
  g.rotateY(yaw);
  g.translate(x, yc, z);
  if (!_batch.has(hex)) _batch.set(hex, []);
  _batch.get(hex).push(g);
}
function guideBeam(a, b) {
  const mx = (a.x + b.x) / 2, mz = (a.z + b.z) / 2;
  const dx = b.x - a.x, dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  const yaw = Math.atan2(dx, dz);
  const y = Math.min(a.y, b.y) - 0.34;
  decoBox(mx, y, mz, 0.12, 0.08, Math.max(0.2, len - 1.15), yaw, GUIDE);
  // Little chevron near the destination end: a readable "go this way" cue.
  const tx = b.x - (dx / len) * 0.72, tz = b.z - (dz / len) * 0.72;
  decoBox(tx, y + 0.08, tz, 0.12, 0.08, 0.62, yaw + 0.62, GUIDE);
  decoBox(tx, y + 0.08, tz, 0.12, 0.08, 0.62, yaw - 0.62, GUIDE);
}
function safetyLedge(x, z, topY, yaw) {
  platform(x, z, topY, 1.9, CHECK);
  // Two tiny visible curb strips make it read as a rest/checkpoint pad without
  // boxing the player in or adding surprise side collisions.
  const sideX = Math.cos(yaw) * 0.78;
  const sideZ = -Math.sin(yaw) * 0.78;
  decoBox(x - sideX, topY + 0.08, z - sideZ, 1.35, 0.12, 0.10, yaw, GUIDE);
  decoBox(x + sideX, topY + 0.08, z + sideZ, 1.35, 0.12, 0.10, yaw, GUIDE);
}
function flushBatch(group) {
  for (const [hex, arr] of _batch) {
    const merged = mergeGeometries(arr, false);
    for (const g of arr) g.dispose();
    const mesh = new THREE.Mesh(merged, new THREE.MeshLambertMaterial({ vertexColors: true }));
    mesh.castShadow = !LOW;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
  _batch.clear();
}

export function buildObby(ctx) {
  _ctx = ctx;
  const C = OBBY_CENTER;

  // ---- The road in: continue the north road to the foot of the mountain.
  ctx.buildPath([[0, 24], [0, 28], [0, 32], [0, 36], [0, 38.5]]);

  // ---- Welcome sign + the lesson, right where the climb begins.
  ctx.addHutSign(0, 38, 'OBBY PEAK', { x: 0, y: 0, z: 38, yaw: 0 });
  ctx.buildSignpost(1.6, 38, [{ text: 'CHOCKYTOWN', yaw: Math.PI }]);
  ctx.addBumpZone(0, 38, 1.7, 'obby-sign', (sourceId) => {
    ctx.showBumpMsg('OBBY PEAK ⛰️', 'Climb to the summit for treasure! Big gaps need a DOUBLE JUMP — tap jump twice. 🆙🆙', 5, sourceId);
  });

  // ---- The course now uses the handmade floating pads as the main read:
  // blue tops, dark undersides, and little spikes make the route feel like a
  // real obby instead of a stack of gray placeholder blocks. They are placed by
  // their standable surface height (floatTopY), so the visual top and collision
  // top stay aligned for jump physics.

  // ---- The central mountain: one tall stone pillar (sheer sides — the spiral
  // is the only way up), capped by the snowy summit pad that holds the prize.
  solid(C.x, PILLAR_TOP / 2, C.z, 2.4, PILLAR_TOP, 2.4, STONE, true);
  solid(C.x, OBBY_SUMMIT_TOP - 0.2, C.z, 3.0, 0.4, 3.0, SNOW, true);   // summit pad (top = OBBY_SUMMIT_TOP)
  solid(C.x - 0.72, OBBY_SUMMIT_TOP + 0.035, C.z - 0.38, 0.7, 0.07, 0.42, 0xdce9ef, false);
  solid(C.x + 0.62, OBBY_SUMMIT_TOP + 0.04, C.z + 0.46, 0.52, 0.08, 0.62, 0xf7fbff, false);
  solid(C.x + 0.08, OBBY_SUMMIT_TOP + 0.032, C.z - 0.86, 0.42, 0.064, 0.36, 0xe8f3f7, false);

  // ---- The climb: a spiral of platforms winding up around the pillar. The two
  // GATE rungs jump a full 2.0 units above the previous rung → double-jump only.
  //                 surface heights, low → high
  const tops = [0.35, 1.00, 1.65, 2.30, 4.30, 4.95, 5.60, 6.25, 8.25, 8.90];
  const gate = [false, false, false, false, true, false, false, false, true, false];
  const N = tops.length;
  const R = 3.4;
  const pads = [];
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 1.85;           // ~0.92 of a turn, climbing
    const x = C.x + Math.sin(ang) * R;
    const z = C.z - Math.cos(ang) * R;              // i=0 starts on the south (entrance) side
    const yaw = -ang + Math.PI * 0.25;              // varied diamonds catch light as you climb
    pads.push({ x, z, y: tops[i], gate: gate[i] });
    if (ctx.placeKitPlatform) {
      ctx.placeKitPlatform('platform_floating', x, z, yaw, tops[i]);
    } else {
      const size = i === 0 ? 2.6 : 1.5;
      platform(x, z, tops[i], size, i === 0 ? BASE : gate[i] ? GATE : STONE);
    }
    // A coin to collect on each rung; gate rungs float their coin higher so the
    // kid aims UP and discovers the double jump.
    ctx.addCoin(x, tops[i] + (gate[i] ? 1.2 : 0.6), z);
  }

  // Route readability + kinder recovery: a low lavender ribbon points pad-to-pad,
  // and two blue rest ledges sit just before each double-jump gate. If a child
  // misses a gate, these catch many falls halfway down instead of sending them
  // all the way to the base.
  for (let i = 1; i < pads.length; i++) guideBeam(pads[i - 1], pads[i]);
  guideBeam(pads[pads.length - 1], { x: C.x, z: C.z, y: OBBY_SUMMIT_TOP });
  safetyLedge((pads[3].x + pads[4].x) / 2, (pads[3].z + pads[4].z) / 2, 3.25, 0.7);
  safetyLedge((pads[7].x + pads[8].x) / 2, (pads[7].z + pads[8].z) / 2, 7.20, -0.55);

  // Extra coach signs at the two big steps. Bump zones are XZ-only, so they work
  // whether the kid is standing on the ledge or landing on the gate pad.
  ctx.addBumpZone((pads[3].x + pads[4].x) / 2, (pads[3].z + pads[4].z) / 2, 1.15, 'obby-gate-1', (sourceId) => {
    ctx.showBumpMsg('DOUBLE JUMP!', 'Tap jump once, then tap again at the top of the bounce. The blue ledge is a safe rest spot.', 4, sourceId);
  });
  ctx.addBumpZone((pads[7].x + pads[8].x) / 2, (pads[7].z + pads[8].z) / 2, 1.15, 'obby-gate-2', (sourceId) => {
    ctx.showBumpMsg('LAST BIG HOP!', 'Use the blue rest ledge, then double jump to the orange pad. You are almost at the cap!', 4, sourceId);
  });

  // ---- The summit treasure: a chest ringed by persistent (one-shot) coins.
  buildChest(ctx, C.x, C.z, OBBY_SUMMIT_TOP);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    ctx.addCoin(C.x + Math.cos(a) * 0.95, OBBY_SUMMIT_TOP + 0.5, C.z + Math.sin(a) * 0.95, true);
  }

  // ---- Alpine dressing around the base (kept outside the spiral, R ~7–9).
  ctx.buildTree(C.x - 7.5, C.z - 5.5, 1.05);
  ctx.buildTree(C.x + 7.0, C.z - 4.0, 0.95);
  ctx.buildTree(C.x - 6.5, C.z + 6.5, 1.1);
  ctx.buildTree(C.x + 8.0, C.z + 3.5, 1.0);
  ctx.buildRock(C.x - 8.5, C.z + 1.5, 1.2);
  ctx.buildRock(C.x + 6.8, C.z + 6.8, 1.0);
  ctx.buildRock(C.x + 4.5, C.z - 7.5, 0.9);
  ctx.buildBush(C.x - 5.0, C.z - 7.0, 0.85);
  ctx.buildMushroomCluster(C.x - 9.0, C.z - 2.0, 3);
  ctx.buildFlowerPatch(C.x + 5.5, C.z - 6.0, 6, 0.8);
  ctx.buildLantern(C.x - 2.2, C.z - 6.5, false);
  ctx.buildLantern(C.x + 2.2, C.z - 6.5, false);

  // One merged mesh per colour for the whole structure (a few draw calls).
  flushBatch(ctx.propsGroup);
}

// A little treasure chest: brown body + lid + gold trim, its own group so
// updateObby() can give it a friendly wiggle. Cosmetic (non-collidable).
function buildChest(ctx, x, z, baseTop) {
  const parts = [];
  const body = tinted(new THREE.BoxGeometry(0.62, 0.4, 0.44), 0x7a4a24); body.translate(0, 0.2, 0);
  const lid  = tinted(new THREE.BoxGeometry(0.64, 0.16, 0.46), 0x8a5a2c); lid.translate(0, 0.46, 0);
  const trim = tinted(new THREE.BoxGeometry(0.66, 0.05, 0.48), 0xf2c14e); trim.translate(0, 0.4, 0);
  const lock = tinted(new THREE.BoxGeometry(0.08, 0.1, 0.05), 0xf2c14e); lock.translate(0, 0.34, 0.24);
  parts.push(body, lid, trim, lock);
  const merged = mergeGeometries(parts, false);
  for (const g of parts) g.dispose();
  const mesh = new THREE.Mesh(merged, new THREE.MeshLambertMaterial({ vertexColors: true }));
  mesh.castShadow = !LOW;
  _chest = new THREE.Group();
  markDynamic(_chest);   // updateObby rocks it
  _chest.add(mesh);
  _chest.position.set(x, baseTop + 0.01, z);
  ctx.propsGroup.add(_chest);
}

export function updateObby(dt, t, playerPos) {
  if (!_ctx) return;
  const dx = playerPos.x - OBBY_CENTER.x, dz = playerPos.z - OBBY_CENTER.z;
  const distSq = dx * dx + dz * dz;
  if (distSq > 2500) return;                  // the zone dozes past ~50 units

  if (_chest) _chest.rotation.y = Math.sin(t * 0.7) * 0.18;

  // Summit reward: granted once you're standing up top (XZ-near-centre AND high
  // enough — not from the ground directly below). Re-arms after you climb down.
  const onSummit = distSq < OBBY_SUMMIT_R * OBBY_SUMMIT_R && playerPos.y > OBBY_SUMMIT_TOP - 0.6;
  if (onSummit && _summitArmed) {
    _summitArmed = false;
    grantSummit();
  } else if (!_summitArmed && playerPos.y < OBBY_SUMMIT_TOP - 3) {
    _summitArmed = true;                      // back down — ready to greet the next climb
  }
}

function grantSummit() {
  const owned = new Set(loadSave().owned);
  if (!owned.has('mountaineer')) {
    owned.add('mountaineer');
    patchSave({ owned: [...owned] });
    if (!getEquipped().head) equip('head', 'mountaineer');
    _ctx.showBumpMsg('⛰️ SUMMIT!', "You conquered Obby Peak! Your MOUNTAINEER CAP is waiting in Chocky's Closet.", 8, 'obby-summit');
  } else {
    _ctx.showBumpMsg('⛰️ SUMMIT!', 'Top of Obby Peak again — nice climbing!', 4, 'obby-summit');
  }
}
